import { IEntity } from '@opengov/api-deprecated';

import { composeEffects } from '../module';
import { transport } from '../transport';

import { entityActionCreators } from './actionsAndReducer';
import { ENTITY_SEARCH_FIELDS } from './records';
import { ICurrentEntity } from './selectors';

const ENTITIES_LIMIT = 10000;

export interface IOptionallyFetchEntityEffect {
  requestedEntitySubdomain: string;
  currentEntity: ICurrentEntity;
  // When true, a failed fetch of an explicitly requested entity is surfaced (rejected)
  // instead of silently falling back to the current entity. Callers that opt in can then
  // handle a real 403/404 deliberately rather than masking it as a redirect to the home
  // entity. Defaults to the legacy silent-fallback behavior when omitted.
  surfaceRequestedEntityError?: boolean;
}

const fetchEntity = async (e: { entityId: string | number }) => {
  return transport
    .url(`/api/v1/entities/${e.entityId}?embed=entity_group`)
    .get()
    .json()
    .then((entity: IEntity): IEntity => {
      entityActionCreators.fetchEntitySuccess(entity);
      return entity;
    });
};

const fetchRequestedEntity = async (e: { entityId: string | number }) => {
  return transport
    .url(`/api/v1/entities/${e.entityId}?embed=entity_group`)
    .get()
    .json()
    .then((entity: IEntity): IEntity => {
      entityActionCreators.fetchRequestedEntitySuccess(entity);
      return entity;
    });
};

const fetchEntities = async () => {
  return transport
    .url(`/api/v3/entities`)
    .query({
      managed_entities: true,
      fields: ENTITY_SEARCH_FIELDS.join(','),
      limit: ENTITIES_LIMIT
    })
    .get()
    .json()
    .then((result) => {
      entityActionCreators.fetchEntitiesSuccess(result.entities);
      return result;
    });
};

export const entityEffects = composeEffects('entity', (compose) => ({
  fetchEntity: compose.effect(fetchEntity),
  fetchEntities: compose.effect(fetchEntities),
  fetchRequestedOrCurrentEntity: compose.effect(async (e: IOptionallyFetchEntityEffect) => {
    if (e.requestedEntitySubdomain !== e.currentEntity.id) {
      // No requested subdomain to resolve: fall back to the current entity.
      if (!e.requestedEntitySubdomain) {
        return fetchEntity({ entityId: e.currentEntity.id });
      }

      const requestedEntity = fetchRequestedEntity({ entityId: e.requestedEntitySubdomain });
      // Opt-in callers want a failed requested-entity fetch surfaced rather than masked
      // as a fallback to the current/home entity.
      if (e.surfaceRequestedEntityError) {
        return requestedEntity;
      }

      return requestedEntity.catch(async () => {
        return fetchEntity({ entityId: e.currentEntity.id });
      });
    }

    return Promise.resolve(e.currentEntity);
  })
}));
